<script setup lang="ts">
/**
 * `/news` — a standalone route rather than a section of the landing page.
 * See `state/news-route.ts` for why this one piece of content gets a real,
 * shareable URL where the rest of the marketing page deliberately does not.
 *
 * The root carries `class="landing"` on purpose: that class is where the
 * palette, the type system and the base element resets live
 * (`landing.css`), and reusing it is what lets this page draw from the same
 * `.news-*` rules the section it replaced already had, with nothing
 * duplicated.
 */
import { computed, onMounted, ref } from 'vue';

import SiteHeader from '../landing/SiteHeader.vue';
import SiteFooter from '../landing/SiteFooter.vue';
import { leaveNewsRoute, navigateNews } from '../state/news-route';
import { fetchNews, newsDisplayDate, type NewsSummary } from '../landing/site-api';
import { useScrolledHeader } from '../landing/scrolled-header';

const emit = defineEmits<{ play: [] }>();

/** See `scrolled-header.ts` — this page has no hero to hand off the header's
 *  logo and background from, so it watches its own sentinel instead. */
const sentinel = ref<HTMLElement | null>(null);
const { scrolled } = useScrolledHeader(sentinel);

/**
 * `SiteHeader`'s "Play free" only ever means "open the sign-in form", which
 * is a fact about the app's session state that this page cannot act on
 * itself — it re-emits for `App.vue` to handle exactly like `LandingScreen`
 * does, and leaves the news route first so the form does not open behind it.
 */
function onPlay(): void {
  leaveNewsRoute();
  emit('play');
}

const newsState = ref<'loading' | 'empty' | 'ready'>('loading');
const newsFeatured = ref<NewsSummary[]>([]);
const newsItems = ref<NewsSummary[]>([]);
const newsPage = ref(1);
const newsTotal = ref(0);
const newsLoadingMore = ref(false);
const newsHasMore = computed(() => newsItems.value.length < newsTotal.value);

/** The page shows the two featured cards plus this many rows; older posts sit in the archive. */
const RECENT_COUNT = 4;
const archiveOpen = ref(false);
const archiveCount = computed(() => Math.max(0, newsTotal.value - RECENT_COUNT));
const shownItems = computed(() =>
  archiveOpen.value ? newsItems.value : newsItems.value.slice(0, RECENT_COUNT),
);

async function loadNews(page: number): Promise<void> {
  const result = await fetchNews(page);
  if (page === 1) {
    newsFeatured.value = result.featured;
    newsItems.value = result.items;
  } else {
    newsItems.value = [...newsItems.value, ...result.items];
  }
  newsPage.value = result.page;
  newsTotal.value = result.total;
  newsState.value = result.featured.length || result.items.length ? 'ready' : 'empty';
}

onMounted(() => {
  void loadNews(1);
});

async function openArchive(): Promise<void> {
  if (newsLoadingMore.value) return;
  newsLoadingMore.value = true;
  try {
    while (newsHasMore.value) {
      const before = newsItems.value.length;
      await loadNews(newsPage.value + 1);
      if (newsItems.value.length === before) break;
    }
    archiveOpen.value = true;
  } finally {
    newsLoadingMore.value = false;
  }
}

function openArticle(slug: string): void {
  navigateNews(`/news/${encodeURIComponent(slug)}`);
}
</script>

<template>
  <div class="landing news-page" data-test="news-list">
    <SiteHeader :scrolled="scrolled" @play="onPlay" />
    <div ref="sentinel" class="scroll-sentinel" aria-hidden="true"></div>

    <main>
      <section class="band">
        <div class="wrap">
          <div class="shead">
            <p class="stamp">From the front office</p>
            <h2>News &amp; updates</h2>
            <p class="ssub">
              Patch notes, new hulls and turrets, event weekends — posted here the moment they go
              live, and not a moment before if they're scheduled ahead.
            </p>
          </div>

          <p v-if="newsState === 'loading'" class="news-note">Fetching the latest…</p>
          <p v-else-if="newsState === 'empty'" class="news-note">
            Nothing posted yet — check back soon.
          </p>
          <template v-else>
            <div v-if="newsFeatured.length" class="news-featured">
              <article
                v-for="post in newsFeatured"
                :key="post.id"
                class="news-card"
                tabindex="0"
                @click="openArticle(post.slug)"
                @keydown.enter="openArticle(post.slug)"
              >
                <div class="news-card-art">
                  <img
                    v-if="post.imageUrl"
                    :src="post.imageUrl"
                    :alt="post.title"
                    loading="lazy"
                    decoding="async"
                  />
                  <span v-else class="plate-blank" aria-hidden="true"></span>
                </div>
                <div class="news-card-body">
                  <p class="news-card-date">{{ newsDisplayDate(post) }}</p>
                  <h3>{{ post.title }}</h3>
                  <p class="news-card-summary">{{ post.summary }}</p>
                  <button
                    type="button"
                    class="btn btn-quiet btn-sm"
                    @click.stop="openArticle(post.slug)"
                  >
                    Read more
                  </button>
                </div>
              </article>
            </div>

            <ul v-if="shownItems.length" class="news-list">
              <template v-for="(post, index) in shownItems" :key="post.id">
              <li v-if="index === RECENT_COUNT" class="news-archive-label" aria-hidden="true">
                Archive
              </li>
              <li
                class="news-row"
                tabindex="0"
                @click="openArticle(post.slug)"
                @keydown.enter="openArticle(post.slug)"
              >
                <div class="news-row-art">
                  <img
                    v-if="post.imageUrl"
                    :src="post.imageUrl"
                    :alt="post.title"
                    loading="lazy"
                    decoding="async"
                  />
                  <span v-else class="plate-blank" aria-hidden="true"></span>
                </div>
                <div class="news-row-body">
                  <p class="news-row-date">{{ newsDisplayDate(post) }}</p>
                  <h3>{{ post.title }}</h3>
                  <p class="news-row-summary">{{ post.summary }}</p>
                </div>
                <button
                  type="button"
                  class="btn btn-quiet btn-sm news-row-cta"
                  @click.stop="openArticle(post.slug)"
                >
                  Read
                </button>
              </li>
              </template>
            </ul>

            <button
              v-if="!archiveOpen && archiveCount > 0"
              type="button"
              class="btn btn-quiet news-more"
              :disabled="newsLoadingMore"
              @click="openArchive"
            >
              {{ newsLoadingMore ? 'Loading…' : `News archive (${archiveCount})` }}
            </button>
          </template>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>
